import { createRouter, createWebHistory } from "vue-router";
import { fetchUserData } from "@/services/account";
import routes from "./routes";
import posthog from 'posthog-js'

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { top: 0 };
        }
    }
});

// Routes that must never be gated by the onboarding/subscription checks
const excludedRoutes = [
    "agency-info",
    "website-info",
    "agency-address",
    "my-agencies",
    "login",
    "plans",
    "register",
    "verify-email",
    "forget-password",
    "password-notification",
    "forbidden"
];

router.beforeEach(async (to, from, next) => {
    if (to.name !== 'login' && to.name !== 'register') {
        document.title = `${to.meta.title} - Tripcart`;
    }
    const APP_URL = import.meta.env.VITE_APP_URL;
    const currentURL = window.location.host;
    const APP_URL_STRIPPED = APP_URL.replace(/^(https?:\/\/)/, '');
    if (to.name == 'NotFound') {
        window.location.href = APP_URL_STRIPPED == currentURL ? '/login' : '/not-found'
    } else {
        if (to.meta.middleware) {
            for (const middleware of to.meta.middleware) {
                await middleware(to, from, next);
            }
        }
        // Check team-management-create route usage
        if (to.name == 'team-management-create') {
            const teamRedirect = resolveTeamManagementRedirect(localStorage.getItem('plansAndUsages'));
            if (teamRedirect) {
                next(teamRedirect);
            } else {
                next();
            }
            return;
        }
        // Onboarding gate: decide verification/onboarding before the route renders,
        // so unverified/unconfigured users never briefly land on the dashboard.
        const onboardingRedirect = await checkOnboarding(to, from);
        if (onboardingRedirect) {
            next(onboardingRedirect);
            return;
        }
        // Refined subscription check
        const canProceed = await checkSubscription(to);
        if (canProceed) {
            next();
        } else {
            next({ name: "plans" });
        }
    }
});

// Automatically track page views
router.afterEach((to) => {
    posthog.capture('$pageview', { current_url: to.fullPath })
})

function safeParseBoolean(key) {
    try {
        return JSON.parse(localStorage.getItem(key));
    } catch (e) {
        return null;
    }
}

/**
 * Decide whether creating a team member is blocked because the agency has hit
 * its plan's team-member limit. Returns a redirect to the team-management list
 * when the quota is full, or null to allow the create route. A missing,
 * malformed, or non-object `plansAndUsages` value resolves to "allow" so the
 * guard always has a decision to act on (and always calls next()).
 *
 * @param {string|null} rawPlansAndUsages The raw localStorage `plansAndUsages` value.
 * @returns {import("vue-router").RouteLocationRaw|null}
 */
export function resolveTeamManagementRedirect(rawPlansAndUsages) {
    let plansAndUsages;
    try {
        plansAndUsages = rawPlansAndUsages ? JSON.parse(rawPlansAndUsages) : {};
    } catch (e) {
        return null; // corrupted value → allow, never hang navigation
    }
    if (plansAndUsages && plansAndUsages.team_used_percentage >= 100) {
        return { name: "team-management" };
    }
    return null;
}

/**
 * Whether a route is exempt from the onboarding/subscription gates (auth and
 * onboarding pages). A missing or blank route name is treated as excluded.
 *
 * @param {import("vue-router").RouteLocationNormalized} to
 * @returns {boolean}
 */
function isExcludedRoute(to) {
    const routeName = typeof to.name === "string" ? to.name.trim() : null;
    return !routeName || excludedRoutes.includes(routeName);
}

/**
 * Decide, before a route renders, whether the authenticated user must be sent
 * into the verification/onboarding flow. Returns a router location to redirect
 * to, or null to allow navigation. Runs inside `beforeEach` so unverified or
 * unconfigured users are never briefly shown the dashboard.
 *
 * @param {import("vue-router").RouteLocationNormalized} to
 * @param {import("vue-router").RouteLocationNormalized} [from]
 * @returns {Promise<import("vue-router").RouteLocationRaw|null>}
 */
export async function checkOnboarding(to, from) {
    // Onboarding/auth routes themselves must not be gated
    if (isExcludedRoute(to)) {
        return null;
    }

    // A logged-out visitor has no session, so the flag checks / fetch below would
    // only 401. Skip and let the axios 401 interceptor handle the login redirect,
    // avoiding a duplicate failed /user request (checkSubscription fires the other).
    if (!safeParseBoolean("isAuthenticated")) {
        return null;
    }

    let isVerified = safeParseBoolean("isVerified");
    let isAgencyConfigured = safeParseBoolean("isAgencyConfigured");

    const flagsMissing =
        typeof isVerified !== "boolean" || typeof isAgencyConfigured !== "boolean";

    // On a full-page load (e.g. the email-verification link redirecting to
    // /dashboard) localStorage may still hold pre-verification flags — the guard
    // runs before fetchUser() refreshes them. Trusting a stale `isVerified: false`
    // would wrongly bounce the just-verified user to the verify-email page. So on
    // the initial navigation, re-validate against the server whenever the cached
    // flags would trigger a redirect. In-app navigations keep trusting localStorage
    // (fetchUser keeps it current) to avoid a fetch on every route change.
    const isInitialNavigation = !from || from.matched.length === 0;
    const flagsWouldRedirect = !isVerified || !isAgencyConfigured;

    if (flagsMissing || (isInitialNavigation && flagsWouldRedirect)) {
        try {
            const response = await fetchUserData();
            const data = response?.data?.data;
            if (!data) {
                return null; // fail open to avoid locking the user out
            }
            isVerified = !!data.email_verified;
            isAgencyConfigured = !!data.agency_configured;
            localStorage.setItem("isVerified", JSON.stringify(isVerified));
            localStorage.setItem("isAgencyConfigured", JSON.stringify(isAgencyConfigured));
            localStorage.setItem("isTrialExpired", JSON.stringify(!!data.is_trial_expired));
            localStorage.setItem("isValidSubscription", JSON.stringify(!!data.is_valid_subscription));
        } catch (e) {
            return null; // fail open on API error
        }
    }

    if (!isVerified) {
        return { name: "verify-email" };
    }
    if (!isAgencyConfigured) {
        return { path: "/agency/agency-info" };
    }
    return null;
}

/**
 * Check if the user's subscription/trial status allows navigation to a route.
 * Returns true to allow navigation, false to block (redirect to plans page).
 *
 * @param {import("vue-router").RouteLocationNormalized} to
 * @returns {Promise<boolean>}
 */
export async function checkSubscription(to) {
    // Missing route name or an excluded route → allow navigation
    if (isExcludedRoute(to)) {
        return true;
    }

    // A logged-out visitor has no session; skip the subscription fetch (it would
    // only 401) and let the 401 interceptor redirect to login.
    if (!safeParseBoolean("isAuthenticated")) {
        return true;
    }

    // Try to get values from localStorage, but if missing, fetch from API
    let isTrialExpired = safeParseBoolean("isTrialExpired");
    let isValidSubscription = safeParseBoolean("isValidSubscription");

    // If values are missing or not boolean, fetch from API
    if (typeof isTrialExpired !== "boolean" || typeof isValidSubscription !== "boolean") {
        try {
            const response = await fetchUserData();
            const data = response?.data?.data;
            if (!data) {
                // If API fails, allow navigation to avoid lockout
                return true;
            }
            isTrialExpired = !!data.is_trial_expired;
            isValidSubscription = !!data.is_valid_subscription;
            localStorage.setItem("isTrialExpired", JSON.stringify(isTrialExpired));
            localStorage.setItem("isValidSubscription", JSON.stringify(isValidSubscription));
        } catch (e) {
            // If API fails, allow navigation to avoid lockout
            return true;
        }
    }

    // If trial is expired and not subscribed, block navigation
    if (isTrialExpired && !isValidSubscription) {
        return false;
    }

    // Otherwise, allow navigation
    return true;
}

export default router;
